import { useEffect, useMemo, useRef } from 'react'
import { RotateCcw } from 'lucide-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from '@/components/LocalizedLink'
import { NewsCard, NewsDialog, NewsMetadata } from '@/components/news'
import { useOverlay } from '@/components/layout/OverlayProvider'
import { contentRoutes } from '@/i18n/content-routes'
import { getLocalizedPath } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'
import { findNewsBySlug, getNewsTranslationKey, localizedNewsRecord, newsArticles, type NewsArticle, type NewsCopy } from '@/data/news'

const ALL_FILTER = 'all'
const NEWS_FILTER = 'news'

export function NewsPage() {
  const { lang } = useParams<{ lang: SupportedLanguage }>()
  const language = (lang === 'en' ? 'en' : 'it') as SupportedLanguage
  const { t } = useTranslation('common')
  const location = useLocation()
  const navigate = useNavigate()
  const { active, open, close } = useOverlay()
  const archiveTitleRef = useRef<HTMLHeadingElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const archiveScrollRef = useRef<number | null>(null)
  const query = useMemo(() => new URLSearchParams(location.search), [location.search])
  const articleSlug = query.get('article')
  const selectedArticle = findNewsBySlug(articleSlug, language)
  const contactQuery = new URLSearchParams({
    subject: t('news.contactSubject'),
    message: t('news.contactMessage'),
  })
  const requestedFilter = query.get('category') ?? ALL_FILTER
  const validFilter = requestedFilter === ALL_FILTER || requestedFilter === NEWS_FILTER
  const articles = validFilter ? newsArticles : []

  useEffect(() => {
    if (selectedArticle) open('news')
    else if (active === 'news') close()
  }, [active, close, open, selectedArticle])
  useEffect(() => {
    if (selectedArticle || archiveScrollRef.current === null) return
    const scroll = archiveScrollRef.current
    archiveScrollRef.current = null
    const frame = requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, scroll)))
    return () => cancelAnimationFrame(frame)
  }, [selectedArticle])

  const updateFilter = (filter: string) => {
    const next = new URLSearchParams(location.search)
    next.delete('article')
    if (filter === ALL_FILTER) next.delete('category')
    else next.set('category', filter)
    navigate({ pathname: location.pathname, search: next.size ? `?${next}` : '' }, { replace: true, state: { preserveScroll: true } })
  }
  const openArticle = (article: NewsArticle, trigger: HTMLElement) => {
    const record = localizedNewsRecord(article, language)
    if (!record) return
    returnFocusRef.current = trigger
    archiveScrollRef.current = window.scrollY
    const next = new URLSearchParams(location.search)
    next.set('article', record.slug)
    navigate({ pathname: location.pathname, search: `?${next}` }, { state: { newsDialog: true, newsScrollY: window.scrollY } })
  }
  const closeArticle = () => {
    if (location.state?.newsDialog) navigate(-1)
    else {
      const next = new URLSearchParams(location.search)
      next.delete('article')
      navigate({ pathname: location.pathname, search: next.size ? `?${next}` : '' }, { replace: true, state: { preserveScroll: true } })
    }
  }

  const unknownArticle = Boolean(articleSlug && !selectedArticle)
  const archivePath = getLocalizedPath('news', language)
  const copy = selectedArticle ? t(getNewsTranslationKey(selectedArticle.id), { returnObjects: true }) as unknown as NewsCopy : null
  const record = selectedArticle ? localizedNewsRecord(selectedArticle, language) : null
  const canonicalPath = record ? `${archivePath}?article=${encodeURIComponent(record.slug)}` : archivePath
  const articleRoute = selectedArticle ? contentRoutes.find((item) => item.type === 'news' && item.id === selectedArticle.id) : null
  const alternatePaths = articleRoute ? { it: articleRoute.it, en: articleRoute.en } : { it: '/it/news', en: '/en/news' }

  return <div className="news-page">
    <NewsMetadata language={language} title={copy?.title ?? t('news.title')} description={copy?.excerpt ?? t('news.metaDescription')} canonicalPath={canonicalPath} alternatePaths={alternatePaths} image={record?.primaryMedia} publishedTime={record?.sourceDate} notFound={unknownArticle} />
    <header className="news-hero internal-hero">
      <div className="shell-container">
        <nav className="catalog-breadcrumb" aria-label={t('news.breadcrumbLabel')}><ol><li><LocalizedLink to="home">{t('nav.home')}</LocalizedLink></li><li aria-current="page"><span>/</span><span>{t('news.title')}</span></li></ol></nav>
        <div className="news-hero-grid">
          <h1 ref={archiveTitleRef} tabIndex={-1}>{t('news.title')}</h1>
          <p>{t('news.introduction')}</p>
        </div>
      </div>
    </header>
    <section className="news-filter-bar" aria-labelledby="news-filter-title">
      <div className="shell-container news-filter-inner">
        <div><h2 id="news-filter-title">{t('news.filterTitle')}</h2><div className="news-filter-chips">
          <button type="button" aria-pressed={requestedFilter === ALL_FILTER} onClick={() => updateFilter(ALL_FILTER)}>{t('news.filterAll')}</button>
          <button type="button" aria-pressed={requestedFilter === NEWS_FILTER} onClick={() => updateFilter(NEWS_FILTER)}>{t('news.categoryNews')}</button>
        </div></div>
        <p className="news-result-count" aria-live="polite">{t('news.resultCount', { count: articles.length })}</p>
      </div>
    </section>
    <div className="shell-container news-archive" role="region" aria-label={t('news.archiveLabel')}>
      {unknownArticle && <div className="news-recovery" role="alert"><div><h2>{t('news.articleNotFoundTitle')}</h2><p>{t('news.articleNotFoundDescription')}</p></div><button type="button" className="button button-secondary" onClick={closeArticle}><RotateCcw aria-hidden="true" />{t('news.backToArchive')}</button></div>}
      {articles.length > 0 ? <div className="news-grid">{articles.map((article) => <NewsCard key={article.id} article={article} language={language} onOpen={openArticle} />)}</div>
        : <div className="news-empty"><h2>{t('news.emptyTitle')}</h2><p>{t('news.emptyDescription')}</p><button type="button" className="button button-secondary" onClick={() => updateFilter(ALL_FILTER)}><RotateCcw aria-hidden="true" />{t('news.resetFilters')}</button></div>}
    </div>
    <section className="news-contact">
      <div className="shell-container"><div><h2>{t('news.contactTitle')}</h2><p>{t('news.contactDescription')}</p></div><LocalizedLink to={`${getLocalizedPath('contacts', language)}?${contactQuery}`} state={{ subject: t('news.contactSubject'), message: t('news.contactMessage') }} className="button">{t('buttons.contactUs')}</LocalizedLink></div>
    </section>
    <NewsDialog article={selectedArticle} language={language} onClose={closeArticle} returnFocus={returnFocusRef.current ?? archiveTitleRef.current} />
  </div>
}
